/**
 * ChoixDeCouleur — choisir une couleur : des pastilles préréglées, une couleur
 * libre (le sélecteur du système), son code hexadécimal.
 *
 *   <ChoixDeCouleur etiquette="Couleur de marque" valeur={marque} onChange={setMarque}
 *     couleurs={BRAND_PRESETS.map((p) => ({ valeur: p.hex, nom: t(p.labelKey) }))} />
 *   <ChoixDeCouleur etiquette="Couleur du rôle" valeur={couleur} onChange={setCouleur} couleurs={PALETTE} taille="sm" />
 *
 * Une pastille choisie porte un anneau d'encre détaché et une coche dans la
 * couleur qui se lit dessus (`contrastFg`) — jamais un voile teinté. C'est un
 * groupe de radios : Tab y entre sur la choisie, ← → passent d'une couleur à
 * l'autre et l'appliquent (un aperçu en direct, comme l'Atelier du thème).
 *
 * Le sélecteur du système émet en continu pendant qu'on glisse : un seul
 * `onChange` par image (rAF), pour ne pas recolorer toute une app à chaque
 * pixel. Un code tapé n'est appliqué qu'une fois valide (`normalizeHex` :
 * `#4d7cff`, `4D7CFF`, `abc`) ; sinon le champ revient à la couleur en place.
 *
 * Successeur des sept `type="color"` du cockpit : l'accent de l'Atelier du
 * thème (`BRAND_PRESETS`), la couleur d'un rôle (`RoleEditorModal`), d'un tag
 * (`fan/TagsEditor`), d'une créatrice (`creatorTabs`), d'un flow, d'un jeu,
 * de l'onboarding (`IdentityStep`).
 */
import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from "react";

import { SousLeChamp } from "../Champ";
import { Check, Hash, Plus } from "../icons";
import { DEFAULT_BRAND, contrastFg, normalizeHex } from "../marque/brandColor";
import { cn } from "../utils";
import "../champs.css";
import "./saisie.css";

export interface CouleurPreselectionnee {
  /** `#rrggbb`. */
  valeur: string;
  /** Son nom, déjà traduit (« Bleu ») : l'infobulle et le lecteur d'écran. */
  nom: string;
}

export interface ChoixDeCouleurProps {
  /** La couleur en place, `#rrggbb` (ou `null` : aucune). */
  valeur: string | null;
  onChange: (hex: string) => void;
  couleurs: readonly CouleurPreselectionnee[];
  etiquette?: ReactNode;
  aide?: ReactNode;
  erreur?: ReactNode;
  /** La couleur libre et son code (défaut vrai). */
  libre?: boolean;
  /** Le nom de la couleur libre — déjà traduit (défaut « Couleur libre »). */
  libreLabel?: string;
  /** Le nom du champ du code (défaut « Code hexadécimal »). */
  codeLabel?: string;
  /** sm 24 px · md 32 px (défaut) · lg 40 px. */
  taille?: "sm" | "md" | "lg";
  disabled?: boolean;
  className?: string;
}

const meme = (a: string | null | undefined, b: string | null | undefined) =>
  !!a && !!b && normalizeHex(a) !== null && normalizeHex(a) === normalizeHex(b);

export function ChoixDeCouleur({
  valeur,
  onChange,
  couleurs,
  etiquette,
  aide,
  erreur,
  libre = true,
  libreLabel = "Couleur libre",
  codeLabel = "Code hexadécimal",
  taille = "md",
  disabled,
  className,
}: ChoixDeCouleurProps) {
  const id = useId();
  const idEtiquette = `${id}-etiquette`;
  const idAide = `${id}-aide`;
  const courante = valeur ? normalizeHex(valeur) : null;
  const indexChoisi = couleurs.findIndex((c) => meme(c.valeur, courante));
  const estLibre = courante !== null && indexChoisi < 0;
  const pastilles = useRef(new Map<number, HTMLButtonElement>());

  const [code, setCode] = useState((courante ?? "").replace(/^#/, ""));
  useEffect(() => setCode((courante ?? "").replace(/^#/, "")), [courante]);

  // Le sélecteur du système : un onChange par image, pas par pixel.
  const image = useRef<number | null>(null);
  const enAttente = useRef<string | null>(null);
  useEffect(() => () => {
    if (image.current !== null) cancelAnimationFrame(image.current);
  }, []);
  const choisirEnContinu = (hex: string) => {
    enAttente.current = hex;
    if (image.current !== null) return;
    image.current = requestAnimationFrame(() => {
      image.current = null;
      if (enAttente.current !== null) onChange(enAttente.current);
    });
  };

  const poserLeCode = (brut: string) => {
    const propre = normalizeHex(brut);
    if (propre) onChange(propre);
    else setCode((courante ?? "").replace(/^#/, ""));
  };

  function auClavier(e: KeyboardEvent<HTMLDivElement>) {
    const sens = e.key === "ArrowRight" || e.key === "ArrowDown" ? 1 : e.key === "ArrowLeft" || e.key === "ArrowUp" ? -1 : 0;
    const bout = e.key === "Home" ? 0 : e.key === "End" ? couleurs.length - 1 : -1;
    if ((sens === 0 && bout < 0) || couleurs.length === 0) return;
    e.preventDefault();
    const depart = indexChoisi < 0 ? (sens > 0 ? -1 : couleurs.length) : indexChoisi;
    const i = bout >= 0 ? bout : (depart + sens + couleurs.length) % couleurs.length;
    onChange(normalizeHex(couleurs[i]!.valeur) ?? couleurs[i]!.valeur);
    pastilles.current.get(i)?.focus();
  }

  return (
    <div className={cn("flex min-w-0 flex-col gap-2", className)}>
      {etiquette !== undefined && (
        <span id={idEtiquette} className="ch-etiquette">
          {etiquette}
        </span>
      )}
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-3">
        <div
          role="radiogroup"
          aria-labelledby={etiquette !== undefined ? idEtiquette : undefined}
          aria-describedby={(erreur ?? aide) !== undefined ? idAide : undefined}
          className="flex flex-wrap items-center gap-2.5"
          onKeyDown={auClavier}
        >
          {couleurs.map((c, i) => {
            const choisie = i === indexChoisi;
            const tabulable = choisie || (indexChoisi < 0 && i === 0);
            return (
              <button
                key={`${c.valeur}-${i}`}
                ref={(el) => {
                  if (el) pastilles.current.set(i, el);
                  else pastilles.current.delete(i);
                }}
                type="button"
                role="radio"
                aria-checked={choisie}
                aria-label={c.nom}
                title={c.nom}
                tabIndex={tabulable ? 0 : -1}
                disabled={disabled}
                data-taille={taille}
                className="si-couleur si-arrondi-couleur"
                style={{ background: c.valeur }}
                onClick={() => onChange(normalizeHex(c.valeur) ?? c.valeur)}
              >
                {choisie && <Check weight="bold" style={{ color: contrastFg(c.valeur) }} aria-hidden />}
              </button>
            );
          })}
        </div>
        {libre && (
          <div className="flex items-center gap-2.5">
            <label
              className={cn("si-couleur si-arrondi-couleur", !estLibre && "si-couleur-libre")}
              data-taille={taille}
              data-choisie={estLibre || undefined}
              title={libreLabel}
              style={estLibre && courante ? { background: courante } : undefined}
            >
              {estLibre && courante ? <Check weight="bold" style={{ color: contrastFg(courante) }} aria-hidden /> : <Plus weight="bold" aria-hidden />}
              <input
                type="color"
                aria-label={libreLabel}
                value={courante ?? DEFAULT_BRAND}
                disabled={disabled}
                onChange={(e) => choisirEnContinu(e.target.value)}
              />
            </label>
            <div
              className={cn("ch-surface ch-arrondi-sm flex h-8 items-center gap-1.5 px-2.5 text-[12.5px]", "w-[7.5rem]")}
              data-eteint={disabled || undefined}
            >
              <Hash className="h-[1.05em] w-[1.05em] shrink-0 text-faint" aria-hidden />
              <input
                className="ch-saisie font-mono uppercase"
                aria-label={codeLabel}
                value={code}
                maxLength={7}
                spellCheck={false}
                autoCapitalize="none"
                autoCorrect="off"
                autoComplete="off"
                disabled={disabled}
                placeholder={DEFAULT_BRAND.replace(/^#/, "").toUpperCase()}
                onChange={(e) => setCode(e.target.value.replace(/^#/, ""))}
                onBlur={() => poserLeCode(code)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    poserLeCode(code);
                  }
                }}
              />
            </div>
          </div>
        )}
      </div>
      <SousLeChamp id={idAide} aide={aide} erreur={erreur} />
    </div>
  );
}
